import React, { useEffect, useState } from 'react'
import TwentyFiveLeftNotif from '~/features/credits-notification/ui'
import ImportStarted from '~/features/checkout-successful/checkout-successful-modal'
import type { Services } from './services/types' // Adjust if needed
import type { GlobalEvent } from '~/types/global-events'
import type { ModalProps } from '~common/components/modal/logic'
import Modal from '~common/components/modal'
import { EmailImporter } from './features/email-importer/ui/importer'
import LoginPermissionsComponent from './features/onboarding/ui/login'
import { UploadTransactionsForm } from './features/transactions/ui/upload-transactions-form'
import { setCredentialsPasswordCookie } from './features/credentials-lock/services/crypto'
import { SettingsModal } from './features/settings/ui'
import { ExportColumnSelection } from './features/export-column-selection'
import PricingComponent from './features/pricing'
import { GetMyInvoicesOnboarding } from './features/plugins/ui/get-my-invoices-onboarding'

interface GlobalModalProps {
    globalEvents: {
        listen: (listener: (event: GlobalEvent) => void) => () => void
    }
    services: Services
}

export const GlobalModal: React.FC<GlobalModalProps> = ({
    globalEvents,
    services,
}) => {
    const [currentModal, setCurrentModal] = useState<{
        modalType: string
        metadata?: Record<string, unknown>
        settings?: Record<string, unknown>
    } | null>(null)

    useEffect(() => {
        const removeListener = globalEvents.listen((event) => {
            if (event.type === 'openModal') {
                setCurrentModal({
                    modalType: event.modalType,
                    metadata: event.metadata,
                    settings: event.settings || {},
                })
            }
        })

        return removeListener
    }, [globalEvents])

    const handleClose = () => {
        setCurrentModal(null)
    }

    if (!currentModal) return null

    const { modalType, metadata, settings } = currentModal

    // Default settings
    const modalSettings: Partial<ModalProps> = {
        size:
            modalType === 'pricing' ||
            modalType === 'transactions-upload' ||
            modalType === 'tutorial-video' ||
            modalType === 'get-my-invoices-onboarding'
                ? 'large'
                : 'medium',
        closeOnOverlayClick: true,
        closeOnEscape: true,
        showCloseButton: true,
        backdropBlur: true,
        enableScroll: modalType === 'pricing' || modalType === 'tutorial-video',
        position: 'center',
        ...(settings as Partial<ModalProps>),
    }

    const renderContent = () => {
        switch (modalType) {
            case 'pricing':
                return (
                    <PricingComponent
                        titleId={(metadata?.offerId as string) || 'default'}
                        onClose={handleClose}
                    />
                )
            case '25-left-notif':
                return (
                    <TwentyFiveLeftNotif
                        onClose={handleClose}
                        offerId={metadata?.offerId as string}
                    />
                )
            case 'import-started':
                return (
                    <ImportStarted
                        message={
                            (metadata?.message as string) || 'Import started!'
                        }
                        onClose={handleClose}
                    />
                )
            case 'checkout-successful-waitlist':
                return (
                    <LoginPermissionsComponent
                        onSuccess={() => {
                            setCurrentModal({
                                modalType: 'auto-scan-modal',
                            })
                        }}
                    />
                )
            case 'gmail-login':
                return (
                    <LoginPermissionsComponent
                        onSuccess={() => {
                            setCurrentModal({
                                modalType: 'auto-scan-modal',
                            })
                        }}
                    />
                )
            case 'auto-scan-modal':
                return <EmailImporter onClose={handleClose} />
            case 'transactions-upload':
                return <UploadTransactionsForm onClose={handleClose} />
            case 'tutorial-video':
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <iframe
                            width="800"
                            height="450"
                            src="https://www.youtube.com/embed/3c-iBn73b6Q"
                            title="Tutorial Video for Recipes"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        ></iframe>
                    </div>
                )
            case 'credentials-password-setup': {
                let inputValue = ''
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                        }}
                    >
                        <h3>Set Credentials Password</h3>
                        <p>
                            This password encrypts your local recipe
                            credentials. Keep it safe. We store it as a secure
                            cookie in this browser only.
                        </p>
                        <input
                            type="password"
                            placeholder="Enter a strong password"
                            onChange={(e) => {
                                inputValue = e.target.value
                            }}
                            style={{ padding: 8 }}
                        />
                        <button
                            onClick={() => {
                                if (!inputValue || inputValue.length < 8) return
                                setCredentialsPasswordCookie(inputValue)
                                handleClose()
                            }}
                        >
                            Save Password
                        </button>
                    </div>
                )
            }
            case 'settings':
                return (
                    <SettingsModal
                        isOpen={true}
                        onClose={handleClose}
                        onOpen={() => {}}
                        openSection={
                            (metadata?.openSection as string) || undefined
                        }
                    />
                )
            case 'export-column-selection':
                return (
                    <ExportColumnSelection
                        exportType={
                            (metadata?.exportType as
                                | 'bank-transactions'
                                | 'invoices') || 'invoices'
                        }
                        onClose={handleClose}
                    />
                )
            case 'get-my-invoices-onboarding':
                return <GetMyInvoicesOnboarding onClose={handleClose} />
            default:
                return null
        }
    }

    return (
        <Modal isOpen={true} onClose={handleClose} {...modalSettings}>
            {renderContent()}
        </Modal>
    )
}
